

import React from 'react';
import { PMIMonthlyData } from '../types';

interface PMIDataTableProps {
  data: PMIMonthlyData[];
}

const formatCurrency = (value: number) => {
    if (Math.abs(value) < 1) value = 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumSignificantDigits: 3,
    }).format(value);
};

export const PMIDataTable: React.FC<PMIDataTableProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return null;
  }
  
  const yearlyDataMap = new Map<number, PMIMonthlyData>();
  data.forEach(row => {
    // End of year snapshot
    if (row.month % 12 === 0 || row.month === data.length) {
      yearlyDataMap.set(row.year, row);
    }
  });

  // Ensure first year is present
  if (!yearlyDataMap.has(data[0].year) && data.length < 12) {
      yearlyDataMap.set(data[0].year, data[data.length-1]);
  }

  const yearlyData = Array.from(yearlyDataMap.values()).sort((a, b) => a.year - b.year);
  
  return (
    <div className="mt-8 bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
      <div className="flex justify-between items-center mb-4 border-b dark:border-slate-600 pb-2">
        <h3 className="text-xl font-bold text-bp-slate dark:text-white">Year-by-Year Wealth Projection</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 border-collapse">
          <thead className="text-xs text-gray-700 dark:text-gray-200 uppercase bg-gray-100 dark:bg-slate-700 sticky top-0">
            <tr>
              <th scope="col" rowSpan={2} className="px-2 py-3 align-middle text-center bg-gray-100 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600 sticky left-0 z-10">Year</th>
              <th scope="col" colSpan={3} className="px-4 py-2 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100">1. Invest Now</th>
              <th scope="col" colSpan={3} className="px-4 py-2 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-green-50 dark:bg-green-900/30 text-green-900 dark:text-green-100">2. Sprint to Cliff</th>
              <th scope="col" colSpan={3} className="px-4 py-2 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100">3. Full Payoff</th>
            </tr>
            <tr>
              {/* Invest Now Columns */}
              <th scope="col" className="px-2 py-2 text-right bg-purple-50 dark:bg-purple-900/20 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-2 text-right bg-purple-50 dark:bg-purple-900/20">Invested</th>
              <th scope="col" className="px-2 py-2 text-right font-bold bg-purple-50 dark:bg-purple-900/20 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
              
              {/* Sprint Columns */}
              <th scope="col" className="px-2 py-2 text-right bg-green-50 dark:bg-green-900/20 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-2 text-right bg-green-50 dark:bg-green-900/20">Invested</th>
              <th scope="col" className="px-2 py-2 text-right font-bold bg-green-50 dark:bg-green-900/20 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
              
              {/* Payoff Columns */}
              <th scope="col" className="px-2 py-2 text-right bg-blue-50 dark:bg-blue-900/20 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-2 text-right bg-blue-50 dark:bg-blue-900/20">Invested</th>
              <th scope="col" className="px-2 py-2 text-right font-bold bg-blue-50 dark:bg-blue-900/20 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
            </tr>
          </thead>
          <tbody>
            {yearlyData.map((row) => (
              <tr key={row.year} className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                <td className="px-2 py-3 font-medium text-gray-900 dark:text-white text-center sticky left-0 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700">{row.year}</td>
                
                {/* Invest Now */}
                <td className="px-2 py-3 text-right text-gray-600 dark:text-gray-300 border-l border-gray-100 dark:border-slate-700">{formatCurrency(row.balances.baseline)}</td>
                <td className="px-2 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(row.investments.baseline)}</td>
                <td className="px-2 py-3 text-right font-semibold text-purple-700 dark:text-purple-400 border-r border-gray-100 dark:border-slate-700">{formatCurrency(row.netWorth.baseline)}</td>
                
                {/* Sprint */}
                <td className="px-2 py-3 text-right text-gray-600 dark:text-gray-300 border-l border-gray-100 dark:border-slate-700">{formatCurrency(row.balances.sprint)}</td>
                <td className="px-2 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(row.investments.sprint)}</td>
                <td className="px-2 py-3 text-right font-semibold text-green-700 dark:text-green-400 border-r border-gray-100 dark:border-slate-700">{formatCurrency(row.netWorth.sprint)}</td>
                
                {/* Payoff */}
                <td className="px-2 py-3 text-right text-gray-600 dark:text-gray-300 border-l border-gray-100 dark:border-slate-700">{formatCurrency(row.balances.payoff)}</td>
                <td className="px-2 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(row.investments.payoff)}</td>
                <td className="px-2 py-3 text-right font-semibold text-blue-700 dark:text-blue-400 border-r border-gray-100 dark:border-slate-700">{formatCurrency(row.netWorth.payoff)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};